import { useId } from "react";
import type { ComposerApi } from "../useRequestComposer";
import type { CatalogBox } from "../catalog";
import { Glyph } from "./Icons";

/**
 * Le confezioni con la foto davanti: «a quale prodotto si riferisce la richiesta»
 * si capisce a colpo d'occhio. Checkbox native (più confezioni) o radio (una
 * sola), dentro card raggruppate per collezione.
 *
 * Il catalogo è un arricchimento: mentre carica si vedono scheletri statici, se
 * non arriva un avviso con «Riprova» e la richiesta si invia lo stesso.
 */

type Mode = "qty" | "multi" | "single";

interface Props {
  api: ComposerApi;
  mode: Mode;
  legend: string;
}

export function BoxPicker({ api, mode, legend }: Props) {
  const uid = useId();
  const { t, catalog } = api;
  const busy = catalog.status === "loading" || catalog.status === "idle";

  return (
    <fieldset className="rq-fieldset rq-group" aria-busy={busy || undefined} data-gb="confezioni">
      <legend className="rq-legend">
        {legend}
        <span className="rq-legend__opt"> {t("contact.guide.optional")}</span>
      </legend>

      {busy && (
        <>
          <span className="rq-sr">{t("contact.guide.catalog.loading")}</span>
          <div className="rq-skel" aria-hidden="true"><span /><span /><span /></div>
        </>
      )}

      {catalog.status === "error" && (
        <div className="rq-callout">
          <Glyph name="info" size={16} />
          <div className="rq-callout__body">
            <span>{t("contact.guide.catalog.error")}</span>
            <button type="button" className="rq-link" onClick={api.retryCatalog}>
              <Glyph name="retry" size={14} />
              {t("common.retry")}
            </button>
          </div>
        </div>
      )}

      {catalog.status === "ready" && catalog.data && catalog.data.groups.map((g, gi) => {
        const gid = `${uid}-g${gi}`;
        return (
          <div key={g.slug ?? "other"} className="rq-coll" role="group" aria-labelledby={gid}>
            <p id={gid} className="rq-coll__title">
              <span className="rq-coll__dot" style={g.color ? { background: g.color } : undefined} aria-hidden="true" />
              {g.title ?? t("contact.guide.catalog.otherGroup")}
            </p>
            <div className="rq-boxes">
              {g.boxes.map((b) => (
                <BoxCard key={b.slug} api={api} box={b} mode={mode} groupName={`${uid}-box`} />
              ))}
            </div>
          </div>
        );
      })}
    </fieldset>
  );
}

function BoxCard({ api, box, mode, groupName }: { api: ComposerApi; box: CatalogBox; mode: Mode; groupName: string }) {
  const uid = useId();
  const { t, state, dispatch } = api;
  const d = state.draft;
  const checked = mode === "single" ? d.box === box.slug : d.boxes.includes(box.slug);
  const nameId = `${uid}-n`;
  const minId = `${uid}-m`;
  const inputId = `${uid}-i`;

  return (
    <div className="rq-box" data-gb={`box-${box.slug}`}>
      <label className="rq-box__pick" htmlFor={inputId}>
        <span className="rq-box__media">
          {box.imageUrl ? (
            <img src={box.imageUrl} alt="" width={328} height={246} loading="lazy" decoding="async" fetchPriority="low" data-flip-id={`img-${box.slug}`} />
          ) : (
            <span className="rq-box__ph"><Glyph name="info" size={18} /></span>
          )}
        </span>
        <span className="rq-box__info">
          <span id={nameId} className="rq-box__name">{box.title}</span>
          {box.moq && <span id={minId} className="rq-box__min">{t("contact.guide.qty.min", { n: box.moq })}</span>}
        </span>
        <input
          id={inputId}
          className="rq-control"
          type={mode === "single" ? "radio" : "checkbox"}
          name={groupName}
          value={box.slug}
          checked={checked}
          aria-labelledby={nameId}
          aria-describedby={box.moq ? minId : undefined}
          onChange={(e) => {
            if (mode === "single") dispatch({ type: "singleBox", slug: box.slug, title: box.title });
            else dispatch({ type: "toggleBox", slug: box.slug, on: e.currentTarget.checked, title: box.title });
          }}
        />
      </label>
      {mode === "qty" && checked && <QtyStepper api={api} box={box} nameId={nameId} minId={box.moq ? minId : undefined} />}
    </div>
  );
}

function QtyStepper({ api, box, nameId, minId }: { api: ComposerApi; box: CatalogBox; nameId: string; minId?: string }) {
  const uid = useId();
  const { t, state, dispatch } = api;
  const raw = state.draft.qty[box.slug] ?? "";
  const n = parseInt(raw, 10);
  const atMin = Number.isNaN(n) || n <= (box.moq ?? 1);
  const raised = state.qtyRaised[box.slug];
  // Mentre si scrive un numero sotto il minimo lo si dice subito (non è un errore):
  // all'uscita dal campo verrà portato al minimo, con un avviso.
  const below = !raised && !!box.moq && !Number.isNaN(n) && n > 0 && n < box.moq;
  const noteId = `${uid}-r`;
  const describedBy = [minId, raised || below ? noteId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className="rq-qty" role="group" aria-labelledby={nameId}>
      <span className="rq-qty__label" aria-hidden="true">{t("contact.guide.qty.label")}</span>
      <span className="rq-qty__ctl">
        <button
          type="button"
          className="rq-qty__btn"
          aria-label={t("contact.guide.qty.decrease", { box: box.title })}
          aria-disabled={atMin || undefined}
          onClick={() => { if (!atMin) dispatch({ type: "qtyStep", slug: box.slug, delta: -1, moq: box.moq }); }}
        >
          <Glyph name="minus" size={14} />
        </button>
        <input
          className="rq-qty__input"
          type="number"
          inputMode="numeric"
          min={box.moq ?? 1}
          step={1}
          placeholder={box.moq ? String(box.moq) : undefined}
          value={raw}
          aria-label={t("contact.guide.qty.input", { box: box.title })}
          aria-describedby={describedBy}
          onChange={(e) => dispatch({ type: "qty", slug: box.slug, raw: e.currentTarget.value })}
          onBlur={() => dispatch({ type: "qtyCommit", slug: box.slug, moq: box.moq })}
        />
        <button
          type="button"
          className="rq-qty__btn"
          aria-label={t("contact.guide.qty.increase", { box: box.title })}
          onClick={() => dispatch({ type: "qtyStep", slug: box.slug, delta: 1, moq: box.moq })}
        >
          <Glyph name="plus" size={14} />
        </button>
      </span>
      {(raised || below) && (
        <span id={noteId} className="rq-qty__note" data-gb="avviso-minimo">
          <Glyph name="info" size={14} />
          {raised ? t("contact.guide.qty.raised", { n: raised }) : t("contact.guide.qty.below", { n: box.moq ?? 0 })}
        </span>
      )}
    </div>
  );
}
